import React, { useState } from "react";
import { useData } from "../../../context/DataContext";
import ImageUploadField from "../../../utils/ImageUploadField";
import { confirmDelete, successToast, errorToast } from "../../../utils/dialog";

const OurChefsAdmin = () => {
  const { ourChefs, addChef, updateChef, deleteChef } = useData();

  const [formData, setFormData] = useState({
    name: "",
    description: "",
    imageUrl: "",
  });
  const [editingId, setEditingId] = useState(null);
  const [imageFile, setImageFile] = useState(null); // Store the selected image file

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      const previewUrl = URL.createObjectURL(file);
      setFormData((prev) => ({ ...prev, imageUrl: previewUrl })); // Show preview
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editingId) {
        await updateChef(editingId, formData, imageFile);
        successToast("Chef updated.");
      } else {
        await addChef(formData, imageFile);
        successToast("Chef added.");
      }
      resetForm();
    } catch (err) {
      console.error(err);
      errorToast("Error saving chef data.");
    }
  };

  const handleEdit = (chef) => {
    setFormData(chef);
    setEditingId(chef.id);
    setImageFile(null); // Reset the image file
  };

  const handleDelete = async (id) => {
    if (
      await confirmDelete({
        title: "Delete chef?",
        text: "This chef will be permanently removed.",
      })
    ) {
      await deleteChef(id);
      successToast("Chef deleted.");
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      description: "",
      imageUrl: "",
    });
    setEditingId(null);
    setImageFile(null);
  };

  return (
    <div className="p-6 space-y-8">
      <h2 className="text-3xl font-bold mb-4 text-gray-800">Our Chefs Admin Panel</h2>

      {/* Form */}
      <form onSubmit={handleSubmit} className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">
          {editingId ? "Edit Chef" : "Add Chef"}
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-[#D48C1E]"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className="w-full p-2 border rounded focus:ring-2 focus:ring-[#D48C1E]"
          />
        </div>
        <ImageUploadField
          label="Photo"
          hint="Recommended size: 300×300px (square)"
          aspect="square"
          previewUrl={formData.imageUrl}
          file={imageFile}
          onChange={handleFileChange}
          onClear={() => {
            setImageFile(null);
            setFormData((prev) => ({ ...prev, imageUrl: "" }));
          }}
        />
        <div className="flex justify-end space-x-4">
          <button
            type="submit"
            className="px-6 py-2 bg-[#14251F] hover:bg-[#1f3a30] text-[#FFD1A0] rounded transition font-medium"
          >
            {editingId ? "Update Chef" : "Add Chef"}
          </button>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="px-6 py-2 bg-gray-500 hover:bg-gray-600 text-white rounded transition"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* List of Chefs */}
      <div className="bg-white p-6 rounded-lg shadow space-y-4">
        <h3 className="text-2xl font-semibold text-gray-700 mb-4">Existing Chefs</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {ourChefs.map((chef) => (
            <div key={chef.id} className="border rounded p-4 shadow">
              <img
                src={chef.imageUrl || "https://via.placeholder.com/150"}
                alt={chef.name}
                className="w-full h-40 object-cover rounded mb-4"
              />
              <h4 className="text-lg font-bold">{chef.name}</h4>
              <p className="text-sm text-gray-600">{chef.description}</p>
              <div className="flex justify-end space-x-2 mt-4">
                <button
                  onClick={() => handleEdit(chef)}
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(chef.id)}
                  className="px-4 py-2 bg-red-500 text-white rounded hover:bg-red-600 transition"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OurChefsAdmin;